import type { CellEntry, Option, Unsubscribe } from "@tento-lona/sheets";
import type { NaiveDate } from "@tento-chrono";
import { ListenerErrors, type ListenerErrorSink, type Thrown } from "@tento-core/listener-errors";
import { Tracing } from "@tento-core/tracing";
import type { RowSyncState } from "./sync-poll-entry.ts";

/** A `subscribe` reader's listener: re-evaluated when a change overlaps its column's days. */
type RangeListener = {
  readonly range: NaiveDate.Range;
  readonly cb: () => void;
};

/**
 * One change of a row's cell set, as a rebuild listener hears it: the days
 * it touched (`range`; `null`: any day, e.g. a whole-row read or an
 * invalidation) and the row's date-keyed cells, read on demand — the whole
 * set (`cells()`, built once per change and only if asked) or only a range
 * (`cellsIn`). A listener that updates by `range` never pays for the row.
 */
export interface RowCellsChange {
  readonly range: Option<NaiveDate.Range>;
  cells(): readonly CellEntry[];
  cellsIn(range: NaiveDate.Range): readonly CellEntry[];
}

/**
 * What a row's subscriptions need from the cache that owns them: the row's
 * name for traces, and a hook run whenever change-listener membership changes
 * (a series projection forwards its source's changes only while something
 * listens — `RowCellCache.bindSourceHandle`), and the host's sink for what
 * listeners threw (`ListenerErrorSink`; `ListenerErrors.RETHROW` when the
 * host named none).
 */
export interface RowSubscriptionsOwner {
  rowLabel(): string;
  listenersChanged(): void;
  listenerErrors(): ListenerErrorSink;
}

/**
 * The listeners of one row cache (`RowCellCache`): change listeners
 * (`onChange`), one token per live `subscribe` reader with its range
 * listener, sync-state listeners and `waitForSync` settlers — and the one
 * fan-out that reaches them.
 *
 * A fan-out's membership is fixed when it starts, a throwing listener never
 * stops the others, and what listeners threw is reported out of band, never
 * into the transaction that notified them (`#report`, through the owner's
 * `ListenerErrorSink`). The owner decides
 * what changed and when; this unit only decides who hears it.
 */
export class RowSubscriptions {
  readonly #owner: RowSubscriptionsOwner;
  readonly #listeners = new Set<() => void>();
  /**
   * One token per live `subscribe` call. Two readers of the same column (two
   * sheet leases over this client-scoped cache) each own a token, so neither
   * replaces nor silences the other; disposing one removes only its own.
   */
  readonly #liveSubscriptions: Set<symbol> = new Set();
  readonly #rangeListeners: Map<symbol, RangeListener> = new Map();
  readonly #syncStateListeners = new Set<(state: Option<RowSyncState>) => void>();
  /** Pending `waitUntil` settlers; `disposeAll` settles them. */
  readonly #syncWaiters = new Set<() => void>();
  /** Told every change of the row's cell set (`onRebuilt`). */
  readonly #rebuildListeners = new Set<(change: RowCellsChange) => void>();

  constructor(owner: RowSubscriptionsOwner) {
    this.#owner = owner;
  }

  /** A change listener, fired by every fan-out. */
  onChange(cb: () => void): Unsubscribe {
    this.#listeners.add(cb);
    this.#owner.listenersChanged();
    return () => {
      this.#listeners.delete(cb);
      this.#owner.listenersChanged();
    };
  }

  /** Any change or range listener registered (what a source-forwarding projection needs). */
  get hasListeners(): boolean {
    return this.#listeners.size > 0 || this.#rangeListeners.size > 0;
  }

  /** Nobody listens: no change, range or sync-state listener and no live reader. */
  get isIdle(): boolean {
    return this.#listeners.size === 0 &&
      this.#liveSubscriptions.size === 0 &&
      this.#syncStateListeners.size === 0;
  }

  /** A fresh live-reader token, named `name` for debugging. */
  acquire(name: string): symbol {
    const token = Symbol(name);
    this.#liveSubscriptions.add(token);
    return token;
  }

  /** The reader `token` is still live (neither released nor retired by `disposeAll`). */
  isLive(token: symbol): boolean {
    return this.#liveSubscriptions.has(token);
  }

  /** Retire the reader `token` (its range listener is removed by the unsubscribe `watchRange` returned). */
  release(token: symbol): void {
    this.#liveSubscriptions.delete(token);
  }

  /** Fire `cb` for every fan-out overlapping `range` while the returned unsubscribe has not run. */
  watchRange(token: symbol, range: NaiveDate.Range, cb: () => void): Unsubscribe {
    this.#rangeListeners.set(token, { range, cb });
    this.#owner.listenersChanged();
    return () => {
      this.#rangeListeners.delete(token);
      this.#owner.listenersChanged();
    };
  }

  /** The distinct ranges live readers watch (a formula row recomputes exactly these). */
  watchedRanges(): NaiveDate.Range[] {
    const ranges = new Map<string, NaiveDate.Range>();
    for (const listener of this.#rangeListeners.values()) {
      ranges.set(`${listener.range.start.dse}:${listener.range.end.dse}`, listener.range);
    }
    return [...ranges.values()];
  }

  /**
   * Told every change of the row's cell set (`fireRebuilt`) until the
   * returned function runs. Not a reader: it keeps no row from eviction, as
   * an index factory does not.
   */
  onRebuilt(listener: (change: RowCellsChange) => void): Unsubscribe {
    this.#rebuildListeners.add(listener);
    return () => {
      this.#rebuildListeners.delete(listener);
    };
  }

  /** Any rebuild listener registered. */
  get hasRebuildListeners(): boolean {
    return this.#rebuildListeners.size > 0;
  }

  /**
   * Tell every rebuild listener `change` (membership fixed at the start);
   * what they threw is reported out of band, never into the rebuild's
   * producer.
   */
  fireRebuilt(change: RowCellsChange): void {
    this.#report(ListenerErrors.fanOut(
      [...this.#rebuildListeners],
      (listener) => this.#rebuildListeners.has(listener),
      (listener) => listener(change),
    ));
  }

  /** A sync-state listener. */
  onSyncState(listener: (state: Option<RowSyncState>) => void): Unsubscribe {
    this.#syncStateListeners.add(listener);
    return () => {
      this.#syncStateListeners.delete(listener);
    };
  }

  /** Resolves on the first change after which `done()` holds — or when `disposeAll` retires the row. */
  waitUntil(done: () => boolean): Promise<void> {
    return new Promise((resolve) => {
      const settle = (): void => {
        this.#syncWaiters.delete(settle);
        unsub();
        resolve();
      };
      const unsub = this.onChange(() => {
        if (done()) settle();
      });
      this.#syncWaiters.add(settle);
    });
  }

  /** Fire every listener (`range == null`), or those overlapping `range`; report what they threw. */
  fire(range: Option<NaiveDate.Range>): void {
    this.#report(this.#dispatch(range));
  }

  /**
   * A sync-state change: every change listener, then the accessor's
   * single-slot hook (`onChanged`), then every sync-state listener with
   * `state` — all of them, even when an earlier one throws.
   */
  fireSyncState(state: Option<RowSyncState>, onChanged: Option<() => void>): void {
    const errors = this.#dispatch(null);
    if (onChanged != null) ListenerErrors.isolate(errors, onChanged);
    ListenerErrors.fanOut(
      [...this.#syncStateListeners],
      (listener) => this.#syncStateListeners.has(listener),
      (listener) => listener(state),
      errors,
    );
    this.#report(errors);
  }

  /** Retire every reader and listener; settle every pending wait. */
  disposeAll(): void {
    this.#liveSubscriptions.clear();
    this.#rangeListeners.clear();
    this.#listeners.clear();
    for (const settle of [...this.#syncWaiters]) settle();
    this.#syncStateListeners.clear();
  }

  /** Rate-limit witness — if listeners fire >30× in 1s on a single
   *  row, something upstream is churning. Caller doesn't skip (we
   *  always want listeners to run); the witness emits for tracing. */
  static readonly #fireListenersRateLimit = new Tracing.Witness
    .RateLimit({
    name: "row-handle/fire-listeners",
    windowMs: 1000,
    threshold: 30,
  });

  /**
   * Feed the churn witness one listener fan-out. A fan-out with no listener
   * runs nothing, so it is not churn and costs nothing to watch.
   */
  #observeFire(): void {
    const listenerCount = this.#listeners.size + this.#rangeListeners.size;
    if (listenerCount === 0) return;
    RowSubscriptions.#fireListenersRateLimit.observe(this.#owner.rowLabel(), { listenerCount });
  }

  /**
   * One listener fan-out (every listener, or the range listeners overlapping
   * `range`). Membership is fixed when it starts: a listener registered by
   * another during the fan-out waits for the next one, and one removed during
   * it is skipped. A throwing listener does not stop the others; what each
   * threw is returned, in order, for the caller to report once every
   * listener has run (`#report`).
   */
  #dispatch(range: Option<NaiveDate.Range>): Thrown[] {
    if (this.#listeners.size === 0 && this.#rangeListeners.size === 0) return [];
    this.#observeFire();
    const listeners = [...this.#listeners];
    const rangeListeners = [...this.#rangeListeners];
    const errors = ListenerErrors.fanOut(
      listeners,
      (listener) => this.#listeners.has(listener),
      (listener) => listener(),
    );
    return ListenerErrors.fanOut(
      rangeListeners,
      ([token, listener]) =>
        this.#rangeListeners.get(token) === listener &&
        (range == null || RowSubscriptions.#rangesOverlap(listener.range, range)),
      ([, listener]) => listener.cb(),
      errors,
    );
  }

  /**
   * Report what a fan-out's listeners threw, OUT OF BAND: a listener is a
   * subscriber, never part of the transaction that notified it (a fetch
   * commit, a bulk install, a write, a sync-state change). The producer
   * finishes and its own callers never see a subscriber's error as their
   * failure; the owner's `ListenerErrorSink` receives it once every listener
   * has run, the one value as it was thrown, several as one `AggregateError`
   * in listener order (`ListenerErrors.report`).
   */
  #report(errors: readonly Thrown[]): void {
    ListenerErrors.report(this.#owner.listenerErrors(), errors, "RowCellCache");
  }

  static #rangesOverlap(
    a: NaiveDate.Range,
    b: NaiveDate.Range,
  ): boolean {
    return a.start.dse < b.end.dse && b.start.dse < a.end.dse;
  }
}
